import React, { useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useGetOnDemandStaffProfessionalCredentialByTypeQuery } from '../../../redux/onDemandStaffProfessionalCredentials';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import OnDemandStaffCredentialActions from './row-actions/OnDemandStaffCredentialActions';
import checkIcon from '../../../assets/images/check.svg';
import redCloseIcon from '../../../assets/images/close-red.svg';

// Expanded sub-table for one credential type. Columns (headers) and rows come from the by-type endpoint,
// which already excludes Status / Rejection Reason and filters to the professional's verified, unexpired,
// ownership-visible credential instances. The Actions column exposes view/download icons that stream the row's
// attachment. Mirrors the agency credentialing sub-table design without reusing its components.
const boolTemplate = (value) =>
	value ? <img src={checkIcon} alt="success" /> : <img src={redCloseIcon} alt="failed" />;

const defaultTemplate = (item, columnData) => {
	const value = item[columnData.field];
	if (typeof value === 'boolean') {
		return boolTemplate(value);
	}
	return <span>{value || '-'}</span>;
};

const listTemplate = (item, columnData) => {
	const list = item[columnData.field] || [];
	if (list.length <= 1) {
		return <span>{list[0]?.name ?? '-'}</span>;
	}
	return (
		<ul className="credential-list">
			{list.map((listItem) => (
				<li key={listItem.name}>{listItem.name}</li>
			))}
		</ul>
	);
};

const OnDemandStaffCredentialData = ({ caregiverId, managementCompanyId, credentialType }) => {
	const { data: credentialData, isFetching } = useGetOnDemandStaffProfessionalCredentialByTypeQuery(
		{ caregiverId, credentialType, managementCompanyId },
		{ skip: !managementCompanyId, refetchOnMountOrArgChange: true }
	);

	const columns = credentialData?.columns || [];
	const rows = credentialData?.data || [];

	const actionsBody = useCallback(
		(rowData) => (
			<OnDemandStaffCredentialActions
				managementCompanyId={managementCompanyId}
				caregiverId={caregiverId}
				fileId={rowData.file_id}
			/>
		),
		[managementCompanyId, caregiverId]
	);

	const renderColumn = useCallback(
		(column) => {
			if (column.renderer === 'edit') {
				return (
					<Column key="actions" header="Actions" alignFrozen="right" body={actionsBody} style={{ width: '110px' }} />
				);
			}
			if (column.renderer === 'list_renderer') {
				return <Column key={column.data} field={column.data} header={column.title} sortable body={listTemplate} />;
			}
			return <Column key={column.data} field={column.data} header={column.title} sortable body={defaultTemplate} />;
		},
		[actionsBody]
	);

	return (
		<DataTable
			value={rows}
			className="credential-data"
			removableSort
			loading={isFetching}
			loadingIcon={<LoadingSpinner />}
			emptyMessage="No results found"
		>
			{columns.map(renderColumn)}
		</DataTable>
	);
};

export default OnDemandStaffCredentialData;
